import { createClient, Client, gql } from "urql";
import { RetryPlugin } from "./plugins/retry.js";
import { CachePlugin } from "./plugins/cache.js";
import { LoggerPlugin } from "./plugins/logger.js";

import type {
  ClientConfig,
  ClientPlugin,
  RequestConfig,
  DefaultPluginOptions,
} from "./types.js";
import type { DocumentNode } from "graphql";
import type { Logger } from "@incubrain/logger";

interface Operation {
  key: string;
  document: DocumentNode;
}

export class BaseClient {
  protected client: Client;
  protected plugins: ClientPlugin[] = [];
  protected logger?: Logger;

  constructor(config: ClientConfig) {
    this.client = createClient({
      url: config.endpoint,
      fetchOptions: {
        headers: config.headers || {},
      },
      exchanges: config.exchanges || [],
    });

    this.logger = config.logger;

    // Initialize default plugins with configuration
    this.initializeDefaultPlugins(config.defaultPluginOptions);

    // Initialize custom plugins
    if (config.plugins) {
      this.plugins.push(...config.plugins);
    }

    this.initializePlugins();
  }

  private initializeDefaultPlugins(options: DefaultPluginOptions = {}) {
    this.plugins.push(
      new RetryPlugin(options.retry),
      new CachePlugin(options.cache),
      new LoggerPlugin(options.logging)
    );
  }

  private async initializePlugins() {
    for (const plugin of this.plugins) {
      if (plugin.extends) {
        for (const [methodName, method] of Object.entries(plugin.extends)) {
          (this as any)[methodName] = method.bind(this);
        }
      }

      if (plugin.onInit) {
        await plugin.onInit(this);
      }
    }
  }

  protected async executeQuery<T>(
    operation: Operation,
    variables?: Record<string, any>
  ): Promise<T> {
    const requestConfig: RequestConfig = {
      operation,
      variables,
      client: this.client,
    };

    try {
      let modifiedConfig = requestConfig;
      for (const plugin of this.plugins) {
        if (plugin.beforeRequest) {
          modifiedConfig = await plugin.beforeRequest(modifiedConfig);
        }
      }

      // Execute the query using urql's `client.query`
      const response = await this.client
        .query<T>(operation.document, variables)
        .toPromise();

      if (response.error) {
        throw response.error;
      }

      for (const plugin of this.plugins) {
        if (plugin.afterRequest) {
          response.data = await plugin.afterRequest(
            response.data,
            modifiedConfig
          );
        }
      }

      return response.data as T;
    } catch (error) {
      for (const plugin of this.plugins) {
        if (plugin.onError) {
          await plugin.onError(error as Error, requestConfig);
        }
      }
      throw error;
    }
  }

  async query<TData = any, TVariables = any>(
    query: string | DocumentNode,
    variables?: TVariables
  ): Promise<TData> {
    if (typeof query === "string") {
      query = gql`
        ${query}
      `;
    }

    return this.executeQuery<TData>(
      {
        key: "raw.query",
        document: query,
      },
      {
        variables,
      }
    );
  }

  async mutation<TData = any, TVariables = any>(
    mutation: string | DocumentNode,
    variables?: TVariables
  ): Promise<TData> {
    if (typeof mutation === "string") {
      mutation = gql`
        ${mutation}
      `;
    }

    return this.executeQuery<TData>(
      {
        key: "raw.mutation",
        document: mutation,
      },
      {
        variables,
      }
    );
  }
}
